import moment from 'moment-timezone';
import { Region, REGION_MAP } from 'services/clients/regions';
import { Shift as PortCollectionShift } from 'types/Port';

export interface Shift {
  day: string;
  shift: string;
}

const SHIFTS_REF: Record<string, PortCollectionShift> = {
  shift1: 'shift1',
  shift2: 'shift2',
  shift3: 'shift3',
};

// TODO: Eventually this should be removed
const DEFAULT_TIMEZONE = 'America/Los_Angeles';
const DEFAULT_REGION = REGION_MAP.USLA;

const regionTimezone = (
  time: string | undefined,
  timezone: Region['timeZone'] = DEFAULT_TIMEZONE
) => {
  return moment(time).tz(timezone);
};

// To take into account that shift 2 ends the following day at 5:30AM
const refDate = (timezone: Region['timeZone'] = DEFAULT_TIMEZONE) => {
  return regionTimezone(undefined, timezone).subtract(5, 'hours').subtract(30, 'minutes');
};

const shiftStartHour = (timezone: Region['timeZone'] = DEFAULT_TIMEZONE) => ({
  shift1: refDate(timezone).set({ hour: 5, minute: 30 }),
  shift2: refDate(timezone).set({ hour: 17, minute: 30 }),
});

const isValidShift = (shift: Shift) => {
  // Avoid shift from Sunday
  if (moment(shift.day).day() === 0) return false;
  // Avoid shift 2 from Saturday
  if (moment(shift.day).day() === 6 && shift.shift === SHIFTS_REF.shift2) return false;
  return true;
};

const nextShift = (shift: Shift, region: Region) => {
  if (shift.shift === SHIFTS_REF.shift1 && region.shifts.includes(SHIFTS_REF.shift2)) {
    return {
      shift: SHIFTS_REF.shift2,
      day: shift.day,
    };
  }
  return {
    shift: SHIFTS_REF.shift1,
    day: moment(shift.day).add(1, 'day').format('YYYY-MM-DD'),
  };
};

export const getShiftEndTime = (
  day: string,
  shift: PortCollectionShift,
  timezone: Region['timeZone'] = DEFAULT_TIMEZONE
) => {
  if (!Object.values(SHIFTS_REF).includes(shift)) throw Error(`Unknown shift value: ${shift}`);
  const date = regionTimezone(`${day}T00:00:00Z`, timezone);
  if (shift === SHIFTS_REF.shift1) date.set({ hour: 17, minute: 30 });
  if (shift === SHIFTS_REF.shift2) date.add(1, 'day').set({ hour: 5, minute: 30 });

  return date;
};

export const getCurrentShift = (region: Region = DEFAULT_REGION) => {
  if (region.shifts.length <= 1) {
    return {
      shift: SHIFTS_REF.shift1,
      day: refDate(region.timeZone).format('YYYY-MM-DD'),
    };
  }

  return {
    shift:
      regionTimezone(undefined, region.timeZone) > shiftStartHour(region.timeZone).shift2
        ? SHIFTS_REF.shift2
        : SHIFTS_REF.shift1,
    day: refDate(region.timeZone).format('YYYY-MM-DD'),
  };
};

export const getAvailableShifts = (nbShift = 4, region: Region = REGION_MAP.USLA) => {
  const availableShifts = [];
  let shift = getCurrentShift(region);
  let maxAttempt = 100;
  do {
    if (isValidShift(shift)) availableShifts.push(shift);
    shift = nextShift(shift, region);
    maxAttempt -= 1;
  } while (availableShifts.length < nbShift && maxAttempt > 0);
  return availableShifts;
};

export const compareShift = (shift1: Shift, shift2: Shift) => {
  if (shift1.day < shift2.day) return true;
  if (shift1.day === shift2.day) return shift1.shift <= shift2.shift;
  return false;
};

export const displayOnlyShift = (shift: string) => {
  if (shift === SHIFTS_REF.shift1) return 'Shift 1';
  if (shift === SHIFTS_REF.shift2) return 'Shift 2';
  if (shift === SHIFTS_REF.shift3) return 'Shift 3';
  throw new Error(`Unknown shift: ${shift}`);
};
export const displayShift = (shift: Shift, justDate = false) => {
  const ds = displayOnlyShift(shift.shift);
  const dateString = moment(shift.day).format('ddd D MMM');
  return justDate ? dateString : `${dateString} - ${ds}`;
};

// TODO: Phase this out in favor of supplying a timezone
export const availableShifts = getAvailableShifts();

export const getFormatedShiftDate = (shiftDate: string) => {
  return moment(shiftDate).format('dddd MMMM DD');
};
